import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MessageSquare, Image, Mic, FileText, Calendar, Trash2, RefreshCw, Grid2x2 } from "lucide-react";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ChatAnalysis {
  id: string;
  file_name: string;
  message_count: number;
  image_count: number;
  audio_count: number;
  document_count: number;
  language: string;
  created_at: string;
  processing_status: string;
  ai_summary?: string;
  short_summary?: string;
  deleted_at?: string | null;
}

const History = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [chats, setChats] = useState<ChatAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [rerunningIds, setRerunningIds] = useState<Set<string>>(new Set());
  const [selectedForMultiView, setSelectedForMultiView] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }

    loadChats();
  }, [user, navigate]);

  const loadChats = async () => {
    try {
      const { data, error } = await supabase
        .from("chat_analyses")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setChats(data || []);
    } catch (error) {
      console.error("Error loading chats:", error);
      toast.error(t("toast.chatHistoryFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      // Soft delete - set deleted_at timestamp instead of hard delete
      const { error } = await supabase
        .from("chat_analyses")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);

      if (error) throw error;

      setChats(chats.map(chat => 
        chat.id === id ? { ...chat, deleted_at: new Date().toISOString() } : chat
      ));
      toast.success(t("toast.chatDeleted"));
    } catch (error) {
      console.error("Error deleting chat:", error);
      toast.error(t("toast.chatDeleteFailed"));
    } finally {
      setDeleteId(null);
    }
  };

  const handleRestore = async (id: string) => {
    try {
      const { error } = await supabase
        .from("chat_analyses")
        .update({ deleted_at: null })
        .eq("id", id);

      if (error) throw error;

      setChats(chats.map(chat => 
        chat.id === id ? { ...chat, deleted_at: null } : chat
      ));
      toast.success(t("toast.chatRestored"));
    } catch (error) {
      console.error("Error restoring chat:", error);
      toast.error(t("toast.chatRestoreFailed"));
    }
  };

  const handleRerunSummary = async (chatId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      // Check budget
      const { data: profile } = await supabase
        .from("profiles")
        .select("budget")
        .eq("id", user!.id)
        .single();

      const rerunCost = 0.005;
      const currentBudget = parseFloat(String(profile?.budget || 0));

      if (currentBudget < rerunCost) {
        toast.error(t("toast.insufficientBudgetRerun"), {
          description: t("toast.addFundsRerun")
        });
        return;
      }

      // Deduct budget
      await supabase
        .from("profiles")
        .update({ budget: currentBudget - rerunCost })
        .eq("id", user!.id);

      setRerunningIds(prev => new Set(prev).add(chatId));
      toast.loading(t("viewer.rerunSummary") + "...");

      const { data, error } = await supabase.functions.invoke("rerun-summary", {
        body: { chatId }
      });

      if (error) throw error;

      // Reload chats to get updated summary
      await loadChats();
      
      toast.dismiss();
      toast.success(t("toast.rerunComplete"));
    } catch (error) {
      console.error("Error rerunning summary:", error);
      toast.dismiss();
      toast.error(t("viewer.rerunSummaryError"));
    } finally {
      setRerunningIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(chatId);
        return newSet;
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-600 dark:text-green-400";
      case "processing":
        return "text-blue-600 dark:text-blue-400";
      case "failed":
        return "text-red-600 dark:text-red-400";
      default:
        return "text-gray-600 dark:text-gray-400";
    }
  };

  const getStatusText = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-background via-background to-accent/5 p-4 sm:p-8 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-64" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-background via-background to-accent/5 p-4 sm:p-8 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              היסטוריית צ'אטים
            </h1>
            <div className="flex gap-2">
              {selectedForMultiView.size > 0 && (
                <Button 
                  onClick={() => navigate(`/multi-view?chats=${Array.from(selectedForMultiView).join(',')}`)}
                  variant="outline"
                >
                  <Grid2x2 className="w-4 h-4 mr-2" />
                  השווה {selectedForMultiView.size} צ'אטים
                </Button>
              )}
              <Button onClick={() => navigate("/upload")} variant="default">
                העלה צ'אט חדש
              </Button>
            </div>
          </div>

          {chats.length === 0 ? (
            <Card className="border-2 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <MessageSquare className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">אין צ'אטים עדיין</h3>
                <p className="text-muted-foreground mb-4">העלה את הצ'אט הראשון שלך מ-WhatsApp כדי להתחיל</p>
                <Button onClick={() => navigate("/upload")}>
                  העלה צ'אט
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Active Chats */}
              <div>
                <h2 className="text-xl font-semibold mb-4">צ'אטים אחרונים</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {chats.filter(chat => !chat.deleted_at).map((chat) => (
                    <Card
                      key={chat.id}
                      className="hover:shadow-lg transition-all duration-300 cursor-pointer group relative"
                    >
                      <div className="absolute top-2 left-2 z-10 flex items-center gap-2">
                        <Checkbox
                          checked={selectedForMultiView.has(chat.id)}
                          onCheckedChange={(checked) => {
                            const newSelected = new Set(selectedForMultiView);
                            if (checked) {
                              newSelected.add(chat.id);
                            } else {
                              newSelected.delete(chat.id);
                            }
                            setSelectedForMultiView(newSelected);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleRerunSummary(chat.id, e)}
                          disabled={rerunningIds.has(chat.id)}
                          title={t("viewer.rerunSummary")}
                        >
                          <RefreshCw className={`h-4 w-4 text-primary ${rerunningIds.has(chat.id) ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(chat.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                      <div 
                        className="w-full h-full"
                        onClick={() => navigate(`/viewer?id=${chat.id}`)}
                      >
                  
                  <CardHeader>
                    <CardTitle className="truncate text-lg">{chat.file_name}</CardTitle>
                    <CardDescription className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {format(new Date(chat.created_at), "MMM d, yyyy")}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className={getStatusColor(chat.processing_status)}>
                        {getStatusText(chat.processing_status)}
                      </span>
                      <span className="text-muted-foreground capitalize">
                        {chat.language}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-sm">
                        <MessageSquare className="h-4 w-4 text-primary" />
                        <span>{chat.message_count} messages</span>
                      </div>
                      
                      {chat.image_count > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <Image className="h-4 w-4 text-green-500" />
                          <span>{chat.image_count} images</span>
                        </div>
                      )}
                      
                      {chat.audio_count > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mic className="h-4 w-4 text-blue-500" />
                          <span>{chat.audio_count} audio</span>
                        </div>
                      )}
                      
                      {chat.document_count > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <FileText className="h-4 w-4 text-orange-500" />
                          <span>{chat.document_count} docs</span>
                        </div>
                      )}
                    </div>

                    {chat.short_summary && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {chat.short_summary}
                      </p>
                    )}
                  </CardContent>
                  </div>
                </Card>
              ))}
                </div>
              </div>

              {/* Deleted Chats */}
              {chats.some(chat => chat.deleted_at) && (
                <div>
                  <h2 className="text-xl font-semibold mb-4">צ'אטים מחוקים</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {chats.filter(chat => chat.deleted_at).map((chat) => (
                      <Card
                        key={chat.id}
                        className="opacity-60 hover:opacity-100 transition-all duration-300 relative border-dashed"
                      >
                        <CardHeader>
                          <CardTitle className="truncate text-lg flex items-center gap-2">
                            {chat.file_name}
                            <span className="text-xs text-destructive">(נמחק)</span>
                          </CardTitle>
                          <CardDescription className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            {format(new Date(chat.created_at), "MMM d, yyyy")}
                          </CardDescription>
                        </CardHeader>
                        
                        <CardContent className="space-y-4">
                          <div className="flex items-center justify-between text-sm">
                            <span className={getStatusColor(chat.processing_status)}>
                              {getStatusText(chat.processing_status)}
                            </span>
                            <span className="text-muted-foreground capitalize">
                              {chat.language}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-center gap-2 text-sm">
                              <MessageSquare className="h-4 w-4 text-primary" />
                              <span>{chat.message_count} messages</span>
                            </div>
                            
                            {chat.image_count > 0 && (
                              <div className="flex items-center gap-2 text-sm">
                                <Image className="h-4 w-4 text-green-500" />
                                <span>{chat.image_count} images</span>
                              </div>
                            )}
                            
                            {chat.audio_count > 0 && (
                              <div className="flex items-center gap-2 text-sm">
                                <Mic className="h-4 w-4 text-blue-500" />
                                <span>{chat.audio_count} audio</span>
                              </div>
                            )}
                            
                            {chat.document_count > 0 && (
                              <div className="flex items-center gap-2 text-sm">
                                <FileText className="h-4 w-4 text-orange-500" />
                                <span>{chat.document_count} docs</span>
                              </div>
                            )}
                          </div>

                          <Button 
                            onClick={() => handleRestore(chat.id)}
                            className="w-full"
                            variant="outline"
                          >
                            שחזר צ'אט
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>למחוק צ'אט?</AlertDialogTitle>
              <AlertDialogDescription>
                הצ'אט הזה יוסר מההיסטוריה שלך. ניתן ליצור קשר עם התמיכה אם תצטרך לשחזר אותו.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>ביטול</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteId && handleDelete(deleteId)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                מחק
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
  );
};

export default History;
